import { create } from "@github/webauthn-json/browser-ponyfill";
import { authApi } from "../api/auth";
import { HGBugsnagNotify } from "./bugsnag";

const PASSKEY_HINT_KEY = "hg_has_passkey";
const PASSKEY_SUGGESTION_KEY = "hg_passkey_suggested_at";
const SUGGESTION_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export function setPasskeyHint() {
  localStorage.setItem(PASSKEY_HINT_KEY, "1");
}

export function hasPasskeyHint(): boolean {
  return localStorage.getItem(PASSKEY_HINT_KEY) === "1";
}

export function shouldSuggestPasskey(): boolean {
  const last = localStorage.getItem(PASSKEY_SUGGESTION_KEY);
  if (!last) return true;
  return Date.now() - Number(last) > SUGGESTION_COOLDOWN_MS;
}

export function markPasskeySuggested() {
  localStorage.setItem(PASSKEY_SUGGESTION_KEY, String(Date.now()));
}

export async function registerPasskey(): Promise<boolean> {
  const credentialCreation = await authApi.webauthnBeginRegister();
  if (
    !credentialCreation.publicKey ||
    !credentialCreation.publicKey.challenge ||
    !credentialCreation.publicKey.rp ||
    !credentialCreation.publicKey.user
  ) {
    console.error("webauthn begin registration invalid response", credentialCreation);
    HGBugsnagNotify("webauthnBegin", new Error("invalid creation response"));
    return false;
  }
  const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
    challenge: credentialCreation.publicKey.challenge,
    rp: credentialCreation.publicKey.rp,
    user: credentialCreation.publicKey.user,
    pubKeyCredParams: [
      { alg: -7, type: "public-key" },
      { alg: -257, type: "public-key" },
    ],
    excludeCredentials: credentialCreation.publicKey?.excludeCredentials,
    authenticatorSelection: {
      authenticatorAttachment: "platform",
      requireResidentKey: true,
      userVerification: "preferred",
    },
  };

  const credential = await create({
    publicKey: publicKeyCredentialCreationOptions,
  });

  await authApi.webauthnFinishRegister(credential);
  setPasskeyHint();
  return true;
}

export async function passkeysSupported(): Promise<boolean> {
  if (
    window.PublicKeyCredential &&
    PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable &&
    PublicKeyCredential.isConditionalMediationAvailable
  ) {
    // Check if user verifying platform authenticator is available.
    const items = await Promise.all([
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(),
      PublicKeyCredential.isConditionalMediationAvailable(),
    ]);

    return items.every((r) => r === true);
  }

  return false;
}
